import React, { useEffect, useState } from "react";
import axios from "axios";
import SyncConstants from "../../constants/SyncConstants";
import useAuthentication from "../../hooks/useAuthentication";

const AllUsersDailyStatsWidget = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [barTooltip, setBarTooltip] = useState({ show: false, content: '', x: 0, y: 0 });
    const [weekOffset, setWeekOffset] = useState(0); // 0 = current week, -1 = last week, etc.
    const Auth = useAuthentication();

    useEffect(() => {
        loadStats();
    }, [weekOffset]);

    const getMonday = (d) => {
        const date = new Date(d);
        const day = date.getDay();
        const diff = date.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
        return new Date(date.setDate(diff));
    };

    const getFriday = (d) => {
        const monday = getMonday(d);
        const friday = new Date(monday);
        friday.setDate(monday.getDate() + 4);
        return friday;
    };

    const getCurrentWeekDates = () => {
        const today = new Date();
        const targetDate = new Date(today);
        targetDate.setDate(today.getDate() + (weekOffset * 7));

        const monday = getMonday(targetDate);
        const friday = getFriday(targetDate);

        return { monday, friday };
    };

    const loadStats = () => {
        setLoading(true);
        const { monday, friday } = getCurrentWeekDates();

        const startDate = monday.toISOString().split('T')[0];
        const endDate = friday.toISOString().split('T')[0];

        axios({
            method: "get",
            url: SyncConstants["syncBaseURL"] + `expenseEntries/allUsersDailyStats?days=5&start_date=${startDate}`,
            auth: Auth.getAuthObject()
        }).then(response => {
            if (response.data.success) {
                setStats(response.data.data);
            }
            setLoading(false);
        }).catch(error => {
            console.error("Error loading daily stats:", error);
            setLoading(false);
        });
    };

    if (loading) {
        return (
            <div className="card shadow-sm h-100">
                <div className="card-body d-flex align-items-center justify-content-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Laden...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!stats || !stats.daily_data) {
        return (
            <div className="card shadow-sm h-100">
                <div className="card-body">
                    <p className="text-muted">Keine Daten verfügbar</p>
                </div>
            </div>
        );
    }

    // Calculate max hours for scaling
    const maxHours = Math.max(...stats.daily_data.map(day => day.hours), 1);
    const scaleMaxHours = Math.ceil(maxHours / 10) * 10; // Round up to nearest 10

    // Calculate total and average - keep precision for display
    const totalMinutes = stats.daily_data.reduce((sum, day) => sum + day.minutes, 0);
    const totalHours = totalMinutes / 60;
    const avgHours = totalHours / stats.daily_data.length;

    // Format function to show hours in HH:MM format
    const formatHours = (hours) => {
        const totalMins = Math.round(hours * 60);
        const h = Math.floor(totalMins / 60);
        const m = totalMins % 60;
        return `${h}:${m.toString().padStart(2, '0')}`;
    };

    return (
        <>
            {/* Bar Tooltip */}
            {barTooltip.show && (
                <div
                    className="position-fixed text-white p-2 rounded-3 shadow-lg"
                    style={{
                        top: `${barTooltip.y}px`,
                        left: `${barTooltip.x}px`,
                        fontSize: '0.75rem',
                        zIndex: 99999,
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                        background: 'rgba(33, 37, 41, 0.95)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        transform: 'translate(-50%, -120%)'
                    }}
                    dangerouslySetInnerHTML={{ __html: barTooltip.content }}
                />
            )}

            <div className="card h-100" style={{
                boxShadow: '0 2px 8px 0 rgba(31, 38, 135, 0.08)',
                backdropFilter: 'blur(10px) saturate(180%)',
                WebkitBackdropFilter: 'blur(10px) saturate(180%)',
                border: '1px solid rgba(255, 255, 255, 0.125)',
                background: 'rgba(15, 23, 42, 0.85)',
                color: '#fff'
            }}>
                <div className="card-body">
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="card-title mb-0 text-white">
                            <i className="bi bi-bar-chart-fill me-2" style={{ color: '#60a5fa' }}></i>
                            Team-Aufwände (Woche)
                        </h5>
                        <div className="d-flex align-items-center gap-2">
                            <button
                                className="btn btn-sm"
                                onClick={() => setWeekOffset(weekOffset - 1)}
                                style={{
                                    background: 'rgba(96, 165, 250, 0.2)',
                                    border: '1px solid rgba(96, 165, 250, 0.3)',
                                    color: '#60a5fa'
                                }}
                            >
                                <i className="bi bi-chevron-left"></i>
                            </button>
                            <span className="text-white small">
                                KW {(() => {
                                    const { monday } = getCurrentWeekDates();
                                    const oneJan = new Date(monday.getFullYear(), 0, 1);
                                    const numberOfDays = Math.floor((monday - oneJan) / (24 * 60 * 60 * 1000));
                                    return Math.ceil((monday.getDay() + 1 + numberOfDays) / 7);
                                })()}
                            </span>
                            <button
                                className="btn btn-sm"
                                onClick={() => setWeekOffset(weekOffset + 1)}
                                disabled={weekOffset >= 0}
                                style={{
                                    background: weekOffset >= 0 ? 'rgba(100, 116, 139, 0.2)' : 'rgba(96, 165, 250, 0.2)',
                                    border: weekOffset >= 0 ? '1px solid rgba(100, 116, 139, 0.3)' : '1px solid rgba(96, 165, 250, 0.3)',
                                    color: weekOffset >= 0 ? '#64748b' : '#60a5fa',
                                    cursor: weekOffset >= 0 ? 'not-allowed' : 'pointer'
                                }}
                            >
                                <i className="bi bi-chevron-right"></i>
                            </button>
                        </div>
                    </div>

                    {/* Summary Stats */}
                    <div className="row g-2 mb-4">
                        <div className="col-6">
                            <div className="text-center p-3 rounded" style={{
                                animation: 'fadeInUp 0.5s ease-out',
                                animationDelay: '0.1s',
                                animationFillMode: 'both',
                                background: 'rgba(96, 165, 250, 0.15)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(96, 165, 250, 0.2)'
                            }}>
                                <div className="h2 mb-0 fw-bold" style={{
                                    animation: 'countUp 0.8s ease-out',
                                    animationDelay: '0.3s',
                                    animationFillMode: 'both',
                                    color: '#93c5fd'
                                }}>
                                    {formatHours(totalHours)}
                                </div>
                                <small style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Gesamt</small>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="text-center p-3 rounded" style={{
                                animation: 'fadeInUp 0.5s ease-out',
                                animationDelay: '0.2s',
                                animationFillMode: 'both',
                                background: 'rgba(52, 211, 153, 0.15)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(52, 211, 153, 0.2)'
                            }}>
                                <div className="h2 mb-0 fw-bold" style={{
                                    animation: 'countUp 0.8s ease-out',
                                    animationDelay: '0.4s',
                                    animationFillMode: 'both',
                                    color: '#6ee7b7'
                                }}>
                                    {formatHours(avgHours)}
                                </div>
                                <small style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Ø pro Tag</small>
                            </div>
                        </div>
                    </div>

                    <style>{`
                        @keyframes fadeInUp {
                            from {
                                opacity: 0;
                                transform: translateY(20px);
                            }
                            to {
                                opacity: 1;
                                transform: translateY(0);
                            }
                        }
                        @keyframes countUp {
                            from {
                                opacity: 0;
                                transform: scale(0.5);
                            }
                            to {
                                opacity: 1;
                                transform: scale(1);
                            }
                        }
                        @keyframes growUp {
                            from {
                                height: 0 !important;
                                opacity: 0;
                            }
                            to {
                                opacity: 1;
                            }
                        }
                    `}</style>

                    {/* Daily Bars Chart */}
                    <div className="mb-3">
                        <h6 className="mb-3 small" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            Aufwände pro Tag (Mo-Fr, {getCurrentWeekDates().monday.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} - {getCurrentWeekDates().friday.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })})
                        </h6>
                        <div className="d-flex align-items-end justify-content-between" style={{ height: '150px', gap: '4px' }}>
                            {stats.daily_data.map((day, index) => {
                                const heightPercent = (day.hours / scaleMaxHours) * 100;
                                const isAboveAverage = day.hours > avgHours;
                                const date = new Date(day.date);
                                const dateStr = date.toLocaleDateString('de-DE', {
                                    day: '2-digit',
                                    month: '2-digit'
                                });
                                const weekday = date.toLocaleDateString('de-DE', { weekday: 'short' });

                                return (
                                    <div
                                        key={day.date}
                                        className="d-flex flex-column justify-content-end align-items-center flex-grow-1"
                                        style={{ height: '100%' }}
                                    >
                                        <div
                                            className="w-100 rounded-3 position-relative overflow-hidden"
                                            style={{
                                                height: `${Math.max(heightPercent, 2)}%`,
                                                backgroundColor: isAboveAverage ? 'rgba(52, 211, 153, 0.3)' : 'rgba(96, 165, 250, 0.3)',
                                                backdropFilter: 'blur(10px)',
                                                border: `1px solid ${isAboveAverage ? 'rgba(52, 211, 153, 0.4)' : 'rgba(96, 165, 250, 0.4)'}`,
                                                transition: 'all 0.2s ease',
                                                cursor: 'pointer',
                                                animation: `growUp 0.6s ease-out ${index * 0.05}s both`
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = isAboveAverage ? 'rgba(52, 211, 153, 0.5)' : 'rgba(96, 165, 250, 0.5)';
                                                e.currentTarget.style.transform = 'translateY(-2px)';

                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setBarTooltip({
                                                    show: true,
                                                    content: `<div class="fw-bold">${weekday}, ${dateStr}</div><div>${day.hours.toFixed(1)}h gesamt</div>`,
                                                    x: rect.left + rect.width / 2,
                                                    y: rect.top
                                                });
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = isAboveAverage ? 'rgba(52, 211, 153, 0.3)' : 'rgba(96, 165, 250, 0.3)';
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                setBarTooltip({ show: false, content: '', x: 0, y: 0 });
                                            }}
                                        >
                                            {/* Subtle shine */}
                                            <div style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                height: '50%',
                                                background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
                                                pointerEvents: 'none'
                                            }}></div>
                                        </div>
                                        <small className="mt-1" style={{ fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                                            {dateStr}
                                        </small>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Summary */}
                    <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <small style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                <i className="bi bi-calendar-range me-1"></i>
                                {new Date(stats.summary.start_date).toLocaleDateString('de-DE')} bis {new Date(stats.summary.end_date).toLocaleDateString('de-DE')}
                            </small>
                            <small style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                <i className="bi bi-bar-chart me-1"></i>
                                {stats.summary.total_days} Tage
                            </small>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AllUsersDailyStatsWidget;
